/**
 * Tracker Stats Component
 *
 * Elegant stat cards for the relationship tracker header.
 * Shows current streak, average rating, and total entry count.
 */

import { Flame, TrendingUp, CalendarDays } from "lucide-react";
import { useRelationshipEntryStats } from "@/hooks/useRelationshipEntries";
import { cn } from "@/lib/utils";

interface TrackerStatsProps {
  startDate: string;
  endDate: string;
}

export function TrackerStats({ startDate, endDate }: TrackerStatsProps) {
  const {
    data: stats,
    isLoading,
    isError,
    refetch,
  } = useRelationshipEntryStats(startDate, endDate);

  if (isLoading) {
    return (
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-[72px] rounded-xl bg-muted/50 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive flex items-center justify-between">
        <span>Failed to load stats</span>
        <button
          onClick={() => refetch()}
          className="text-xs underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const items = [
    {
      icon: Flame,
      value: stats?.current_streak ?? "\u2014",
      label: "day streak",
      iconColor: "text-orange-500",
      bgColor: "bg-orange-500/10 dark:bg-orange-600/10",
    },
    {
      icon: TrendingUp,
      value:
        stats?.average_rating != null
          ? stats.average_rating.toFixed(1)
          : "\u2014",
      label: "avg rating",
      iconColor: "text-blue-500",
      bgColor: "bg-blue-500/10 dark:bg-blue-600/10",
    },
    {
      icon: CalendarDays,
      value: stats?.count ?? "\u2014",
      label: "entries",
      iconColor: "text-emerald-500",
      bgColor: "bg-emerald-500/10 dark:bg-emerald-600/10",
    },
  ];

  return (
    <div className="grid grid-cols-3 gap-3">
      {items.map((item) => (
        <div
          key={item.label}
          className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3"
        >
          <div className={cn("rounded-lg p-2", item.bgColor)}>
            <item.icon className={cn("h-4 w-4", item.iconColor)} />
          </div>
          <div className="min-w-0">
            <div className="text-lg font-semibold leading-tight truncate">
              {item.value}
            </div>
            <div className="text-[11px] text-muted-foreground leading-tight">
              {item.label}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
